import {
  collection,
  doc,
  orderBy,
  query,
  UpdateData,
  updateDoc,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import { DynamicPageVariant } from '@superfunnel/types/dynamic-page-variant';
import {
  DynamicPageExperimentsStatsResponse,
  ExperimentEndMode,
  ExperimentMetricConfig,
  GetExperimentsStatsResponse,
} from '@superfunnel/types/experiment';

import { db, functions } from '../Firebase';
import { emptyConverter } from '../utils';
import { updateDynamicPageHasPendingPublishChanges } from './DynamicPagesService';

export interface StartExperimentTestInput {
  id: string;
  description: string;
  trafficSplit: number;
  /**
   * Pre-created arm website (authored before the experiment). When set, the
   * backend reuses this existing draft website as the arm instead of cloning
   * the control.
   */
  websiteId?: string;
}

/**
 * Test arm for updating an experiment. Existing arms carry their `websiteId` so
 * the backend keeps their website; new arms omit it and get a website cloned
 * from the control.
 */
export interface UpdateExperimentTestInput extends StartExperimentTestInput {
  websiteId?: string;
}

export interface StartExperimentInput {
  name: string;
  metric: ExperimentMetricConfig;
  secondaryMetrics: ExperimentMetricConfig[];
  endMode: ExperimentEndMode;
  endModeValue?: number;
  minVisitorsPerVariant?: number;
  minDurationDays?: number;
  tests: StartExperimentTestInput[];
}

export interface UpdateExperimentInput {
  name?: string;
  metric?: ExperimentMetricConfig;
  secondaryMetrics?: ExperimentMetricConfig[];
  endMode?: ExperimentEndMode;
  endModeValue?: number | null;
  minVisitorsPerVariant?: number | null;
  minDurationDays?: number | null;
  tests?: UpdateExperimentTestInput[];
  websiteIdsToCreate?: string[];
  websiteIdsToRemove?: string[];
}

/**
 * Optional settings applied to a newly-created variant when it is added or
 * duplicated (so the agent's chosen name/status/description/path are set at
 * create time and the server-generated id is used as-is).
 */
export interface VariantFieldOverrides {
  name?: string | null;
  description?: string | null;
  status?: 'draft' | 'active' | 'disabled' | null;
  path?: string | null;
}

export const dynamicPageVariantsColRef = (
  accountId: string,
  dynamicPageId: string,
) =>
  collection(
    db,
    'accounts',
    accountId,
    'campaigns',
    dynamicPageId,
    'variants',
  ).withConverter(emptyConverter<DynamicPageVariant>());

export const getDynamicPageVariantsQuery = (
  accountId: string,
  dynamicPageId: string,
) =>
  query(
    dynamicPageVariantsColRef(accountId, dynamicPageId),
    where('status', 'in', ['draft', 'active', 'disabled']),
    orderBy('createdAt', 'asc'),
  );

export const duplicateWebsiteAsVariant = async (
  accountId: string,
  dynamicPageId: string,
  variantId: string,
  websiteId: string,
  overrides?: VariantFieldOverrides,
  snapshotId?: string,
): Promise<{ ok: boolean; variantId: string; websiteId: string }> => {
  const fn = httpsCallable<
    unknown,
    { ok: boolean; variantId: string; websiteId: string }
  >(functions, 'accounts/campaigns/experiments/duplicateWebsiteAsVariant');

  const res = await fn({
    accountId,
    campaignId: dynamicPageId,
    variantId,
    websiteId,
    overrides,
    snapshotId,
  });

  updateDynamicPageHasPendingPublishChanges(accountId, dynamicPageId, true);

  return res.data;
};

export const startExperiment = async (
  accountId: string,
  dynamicPageId: string,
  variantId: string,
  experimentData: StartExperimentInput,
): Promise<{ ok: boolean; experimentId: string }> => {
  const fn = httpsCallable<unknown, { ok: boolean; experimentId: string }>(
    functions,
    'accounts/campaigns/experiments/startExperiment',
  );

  const res = await fn({
    accountId,
    campaignId: dynamicPageId,
    variantId,
    ...experimentData,
  });

  updateDynamicPageHasPendingPublishChanges(accountId, dynamicPageId, true);

  return res.data;
};

export const updateExperiment = async (
  accountId: string,
  dynamicPageId: string,
  variantId: string,
  experimentId: string,
  experimentData: UpdateExperimentInput,
): Promise<{ ok: boolean }> => {
  const fn = httpsCallable<unknown, { ok: boolean }>(
    functions,
    'accounts/campaigns/experiments/updateExperiment',
  );

  const res = await fn({
    accountId,
    campaignId: dynamicPageId,
    variantId,
    experimentId,
    ...experimentData,
  });

  updateDynamicPageHasPendingPublishChanges(accountId, dynamicPageId, true);

  return res.data;
};

export const endExperiment = async (
  accountId: string,
  dynamicPageId: string,
  variantId: string,
  winnerWebsiteId: string,
): Promise<any> => {
  const fn = httpsCallable(
    functions,
    'accounts/campaigns/experiments/endExperiment',
  );

  const res = await fn({
    accountId,
    campaignId: dynamicPageId,
    variantId,
    winnerWebsiteId,
  });

  updateDynamicPageHasPendingPublishChanges(accountId, dynamicPageId, true);

  return res.data;
};

export const addVariant = async (
  accountId: string,
  dynamicPageId: string,
  input: {
    name: string;
    description?: string | null;
    status?: 'draft' | 'active' | 'disabled' | null;
    path?: string | null;
    websiteId?: string | null;
    constraints?: DynamicPageVariant['constraints'] | null;
  },
): Promise<{ variantId: string; websiteId: string }> => {
  const fn = httpsCallable<unknown, { variantId: string; websiteId: string }>(
    functions,
    'accounts/campaigns/addVariant',
  );

  const res = await fn({
    accountId,
    dynamicPageId,
    ...input,
  });

  updateDynamicPageHasPendingPublishChanges(accountId, dynamicPageId, true);

  return res.data;
};

export const setDefaultVariant = async (
  accountId: string,
  dynamicPageId: string,
  variantId: string,
) => {
  const fn = httpsCallable(functions, 'accounts/campaigns/setDefaultVariant');
  await fn({ accountId, dynamicPageId, variantId });
};

export const duplicateVariant = async (
  accountId: string,
  dynamicPageId: string,
  variantId: string,
  overrides?: VariantFieldOverrides,
): Promise<{ ok: boolean; variantId: string }> => {
  const fn = httpsCallable<unknown, { ok: boolean; variantId: string }>(
    functions,
    'accounts/campaigns/duplicateVariant',
  );

  const res = await fn({
    accountId,
    campaignId: dynamicPageId,
    variantId,
    overrides,
  });

  updateDynamicPageHasPendingPublishChanges(accountId, dynamicPageId, true);

  return res.data;
};

export const duplicateVariantAsNewDynamicPage = async (
  accountId: string,
  dynamicPageId: string,
  variantId: string,
): Promise<any> => {
  const fn = httpsCallable(
    functions,
    'accounts/campaigns/duplicateVariantAsNewDynamicPage',
  );

  const res = await fn({
    accountId,
    campaignId: dynamicPageId,
    variantId,
  });

  return res.data;
};

export const updateVariant = async (
  accountId: string,
  dynamicPageId: string,
  variantId: string,
  update: UpdateData<DynamicPageVariant>,
): Promise<any> => {
  const varaintDocRef = doc(
    dynamicPageVariantsColRef(accountId, dynamicPageId),
    variantId,
  );

  await updateDoc(varaintDocRef, update);

  updateDynamicPageHasPendingPublishChanges(accountId, dynamicPageId, true);
};

export interface GetExperimentsStatsParams {
  accountId: string;
  /** Optional - if not provided, fetches all experiments across all dynamic pages */
  dynamicPageId?: string | null;
  /** Optional - filter by experiment status. If not provided, fetches all. */
  status?: 'running' | 'completed' | 'cancelled' | null;
}

/**
 * Fetch experiments stats for a dynamic page or all dynamic pages.
 * Returns extended stats with dynamic page info and summary counts.
 */
export const getExperimentsStats = async (
  params: GetExperimentsStatsParams,
): Promise<GetExperimentsStatsResponse> => {
  const fn = httpsCallable<
    GetExperimentsStatsParams,
    GetExperimentsStatsResponse
  >(functions, 'accounts/campaigns/experiments/getExperimentsStats');

  const res = await fn(params);

  return res.data;
};

/**
 * Legacy function for backward compatibility with existing code.
 * @deprecated Use getExperimentsStats with params object instead
 */
export const getExperimentsStatsForDynamicPage = async (
  accountId: string,
  dynamicPageId: string,
): Promise<DynamicPageExperimentsStatsResponse> => {
  const result = await getExperimentsStats({ accountId, dynamicPageId });
  // Convert to the old format for backward compatibility
  // ExperimentStatsWithPageInfo extends ExperimentStats so this is safe
  return result.experiments as unknown as DynamicPageExperimentsStatsResponse;
};
